import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

function renderContent(content: string) {
  return content.split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={i} />;
    if (trimmed.startsWith('## ')) {
      return <h2 key={i} className="text-xl font-semibold text-foreground-900 mt-8 mb-4 font-heading">{trimmed.replace('## ', '')}</h2>;
    }
    if (trimmed.startsWith('### ')) {
      return <h3 key={i} className="text-lg font-semibold text-foreground-900 mt-6 mb-3 font-heading">{trimmed.replace('### ', '')}</h3>;
    }
    if (trimmed.startsWith('- **')) {
      const match = trimmed.match(/^- \*\*(.+?)\*\*[：:]?\s*(.*)/);
      if (match) {
        return (
          <div key={i} className="flex items-start gap-2 ml-4 mb-2">
            <span className="text-primary-500 mt-1 flex-shrink-0">•</span>
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong>{match[1]}</strong>：{match[2]}
            </p>
          </div>
        );
      }
    }
    if (/^\d+\.\s/.test(trimmed)) {
      return <p key={i} className="text-sm text-foreground-700 leading-relaxed ml-4 mb-2">{trimmed}</p>;
    }
    return <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{trimmed}</p>;
  });
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <PageMeta
        title={`${article.title} - 爱威奶在线`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.aiweinai.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.coverImage}
      />

      <Navbar />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={[
            { label: '首页', to: '/' },
            { label: '文章资讯', to: '/articles' },
            { label: article.title },
          ]} />

          {/* Article Header */}
          <header className="mt-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <time dateTime={article.date}>{article.date}</time>
            </div>
          </header>

          {/* Cover Image */}
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 爱威奶在线`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Body */}
          <div className="prose-custom max-w-none">
            {renderContent(article.content)}
          </div>

          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap mt-10 pt-8 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-16">
              <h2 className="text-xl font-semibold text-foreground-900 mb-6 font-heading">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.coverImage,
          "author": { "@type": "Person", "name": article.author },
          "datePublished": article.date,
          "publisher": {
            "@type": "Organization",
            "name": "爱威奶在线",
            "url": "https://www.aiweinai.com",
          },
          "url": `https://www.aiweinai.com/article/${article.slug}`,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://www.aiweinai.com/article/${article.slug}`,
          },
        }),
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.aiweinai.com" },
            { "@type": "ListItem", "position": 2, "name": "文章资讯", "item": "https://www.aiweinai.com/articles" },
            { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://www.aiweinai.com/article/${article.slug}` },
          ],
        }),
      }} />
    </>
  );
}